import React, { useEffect, useRef, useState } from "react";
import { Col, Container } from "reactstrap";
import { Html5Qrcode } from "html5-qrcode";
import Section from "./Section";
import './qrscanner.css';

const QrScanner = () => {
  const [scanResult, setScanResult] = useState(null);  
  const qrCodeRegionRef = useRef(null);
  const html5QrCodeInstance = useRef(null);

  const [fishDetails, setFishDetails] = useState({
    fishType: '',
    quantity: 0,
    qualityGrade: '',
    capturedLocation: '',
    capturedDate: '',
    totalStock: 0,
    storageCondition: '',
    stockUpdatedDate: '',
    orderId: '',
    orderStatus: '',
    orderQuantity: 0,
    vesselName: '',
    vesselLocation: '',
    equipment: '',
    ownerName: '',
    ownerContact: '',
    sellingPrice: 0
  });

  useEffect(() => {
    if (qrCodeRegionRef.current) {
      html5QrCodeInstance.current = new Html5Qrcode(qrCodeRegionRef.current.id);
      
      html5QrCodeInstance.current
        .start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            setScanResult(decodedText);
            updateFishDetails(decodedText); // Parse and update fishDetails from the QR code result
            stopScanner();
          },
          (errorMessage) => {
            console.warn(errorMessage);
          }
        )
        .catch((error) => console.error("Failed to start QR scanner", error));
    }

    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = () => {
    if (html5QrCodeInstance.current) {
      html5QrCodeInstance.current.stop().then(() => {
        html5QrCodeInstance.current = null;
      }).catch((error) => console.error("Failed to stop QR scanner", error));
    }
  };

  const updateFishDetails = (decodedText) => {
    try {
      const parsedData = JSON.parse(decodedText); // Parse JSON data from the QR code
      setFishDetails(parsedData); // Set the parsed data to fishDetails
    } catch (error) {
      console.error("Invalid QR code data format", error);
    }
  };

  return (
    <React.Fragment>
      <Section />
      <section className="section">
        <Container>
          <Col md={6} className="mx-auto text-center">
            <div id="qr-code-region" ref={qrCodeRegionRef}></div>
            {scanResult && (
              <div>
                <p className="mt-4">
                  <strong>Scanned QR Code:</strong> {scanResult}
                </p>
                <div className="container scanned-details">
                  {/* Fish Details Card */}
                  <div className="card  shadow mb-4">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="fa fa-fish me-2" style={{ fontSize: '1.5rem' }}></i> Fish Details 
                      </h5>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Type of Fish:</strong> <span>{fishDetails.fishType}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Quantity:</strong> <span>{fishDetails.quantity} kg</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Quality Grade:</strong> <span>{fishDetails.qualityGrade}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Captured Location:</strong> <span>{fishDetails.capturedLocation}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Captured Date:</strong> <span>{fishDetails.capturedDate}</span>
                        </li>
                      </ul>
                      <a href="/stockdetails">More Details</a>
                    </div>
                  </div>

                  {/* Stock Details Card */}
                  <div className="card shadow mb-4">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="uil uil-box me-2" style={{ fontSize: '1.5rem' }}></i> Stock Details
                      </h5>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Total Stock:</strong> <span>{fishDetails.totalStock} kg</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Storage Condition:</strong> <span>{fishDetails.storageCondition}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Stock Updated Date:</strong> <span>{fishDetails.stockUpdatedDate}</span>
                        </li>
                      </ul>
                      <a href="/stockdetails">More Details</a>
                    </div>
                  </div>

                  {/* Order Details Card */}
                  <div className="card shadow mb-4">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="uil uil-receipt me-2" style={{ fontSize: '1.5rem' }}></i> Order Details
                      </h5>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Order ID:</strong> <span>{fishDetails.orderId}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Order Status:</strong> <span>{fishDetails.orderStatus}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Order Quantity:</strong> <span>{fishDetails.orderQuantity} kg</span>
                        </li>
                      </ul>
                      <a href="/orderdetails">More Details</a>
                    </div>
                  </div>

                  {/* Vessel Details Card */}
                  <div className="card shadow mb-4">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="uil uil-anchor me-2" style={{ fontSize: '1.5rem' }}></i> Vessel Details
                      </h5>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Vessel Name:</strong> <span>{fishDetails.vesselName}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Current Location:</strong> <span>{fishDetails.vesselLocation}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Equipment Available:</strong> <span>{fishDetails.equipment}</span>
                        </li>
                      </ul>
                      <a href="/vesseldetails">More Details</a>
                    </div>
                  </div>

                  {/* Vessel Owner Details Card */}
                  <div className="card shadow mb-4">
                    <div className="card-body">
                      <h5 className="card-title d-flex align-items-center">
                        <i className="uil uil-user me-2" style={{ fontSize: '1.5rem' }}></i> Vessel Owner Details
                      </h5>
                      <ul className="list-group list-group-flush">
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Owner Name:</strong> <span>{fishDetails.ownerName}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Contact:</strong> <span>{fishDetails.ownerContact}</span>
                        </li>
                        <li className="list-group-item d-flex justify-content-between align-items-center">
                          <strong>Selling Price:</strong> <span>{fishDetails.sellingPrice} per kg</span>
                        </li>
                      </ul>
                      <a href="/ownerdetails">More Details</a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default QrScanner;
